import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalSubTitle } from '../comps';
import { Poppins, Spacer } from 'src/common';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { Guard } from '../Guard';
import _ from 'lodash';
import TabsMenu from 'src/components/TabsMenu';
import { FromExisting } from './comps';
import { motion } from 'framer-motion';
import IncidentForm from 'src/components/formComposed/IncidentForm';
import { FormikProvider, useFormik } from 'formik';
import { incidentInitialValues, incidentValidationSchema } from 'src/components/formComposed/IncidentForm/util';
import { selectUserOption } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { useLinkScenarioToIncidentsMutation, useUpsertIncidentsMutation } from 'src/graphql/_generated';
import { KeyType, useCache } from 'src/graphql';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

const StyledModal = styled(Modal)`
  width: 1568px;
  min-height: 200px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .content {
    overflow-y: auto;
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }
`;

interface LinkIncidentsToScenarioModalProps {
  onClose: () => void;
  invalidateQueries: KeyType[];
  scenarioId: string;
  subTitle?: {
    title: string;
    value: string;
  };
}

export const LinkIncidentsToScenarioModal: React.FC<LinkIncidentsToScenarioModalProps> = ({
  onClose,
  invalidateQueries,
  scenarioId,
  subTitle,
}) => {
  const [activeTab, setActiveTab] = useState<'from_new' | 'from_existing'>('from_new');
  const [err, setErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [checked, setChecked] = useState<string[]>([]);
  const [responseErr, setResponseErr] = useState('');
  const { usersOptios, workspaceId } = useAuth();
  const { refetchQuery } = useCache();

  const [isProcessing, setIsProcessing] = useState(false);

  const [linkScenarioToIncidents] = useLinkScenarioToIncidentsMutation({
    onError: (err: any) => {
      setErr(err.message || 'Something went wrong');
      setIsProcessing(false);
    },
  });

  const afterSuccess = async () => {
    queryCache.invalidateQueries([QKeys.Scenario, { scenarioId, variant: 'base' }]);
    queryCache.invalidateQueries([QKeys.Audit]);
    await refetchQuery({ include: invalidateQueries });
    showSuccessToast(toastMessages.incident.linked);
    onClose();
  };

  const [upsertIncidents] = useUpsertIncidentsMutation();

  const formik = useFormik({
    initialValues: incidentInitialValues,
    validationSchema: incidentValidationSchema,
    validateOnMount: true,
    enableReinitialize: true,

    onSubmit: async (values) => {
      setResponseErr('');

      const { userAdd } = selectUserOption({
        usersOptios,
        selectedUser: values.owner,
      });

      const incident = await upsertIncidents({
        variables: { incidentsData: [{ ...values, owner: userAdd }], workspaceId },
      }).catch((err) => setResponseErr(err.message || 'Something went wrong'));

      if (!incident?.data?.upsertIncidents?.length) {
        return;
      }

      await linkScenarioToIncidents({
        variables: {
          incidentIds: [incident.data?.upsertIncidents[0]?.id!],
          scenarioId: scenarioId,
        },
      });
      await afterSuccess();
    },
  });

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={!!checked.length || formik.dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen center={false} persistent={isProcessing || formik.isSubmitting}>
          <div className="content">
            <Spacer $px={40} />
            <div className="h-padding">
              <Poppins className="m-title" px={28}>
                Link Incidents
              </Poppins>
              {subTitle && <ModalSubTitle title={subTitle.title} value={subTitle.value} />}
              <Spacer $px={20} />
              <TabsMenu
                tabs={[
                  { label: 'Enter Incident Details', value: 'from_new', dataCy: 'from_new' },
                  { label: 'Link Exisiting Incidents', value: 'from_existing', dataCy: 'from_existing' },
                ]}
                activeTab={activeTab}
                setActiveTab={(tab) => {
                  setActiveTab(tab);
                  mpEvent(MPEvents.ButtonClick, {
                    button: tab === 'from_new' ? 'Tab:Enter Incident Details' : 'Tab:Link Exisiting Incidents',
                    modal: 'Link incidents to scenario modal',
                    tags: ['SCENARIO', 'INCIDENT'],
                  });
                }}
              />
              <Spacer $px={40} />
              {activeTab === 'from_existing' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0, duration: 0.1 }}>
                  <FromExisting
                    scenarioId={scenarioId}
                    setChecked={setChecked}
                    isLoading={isProcessing}
                    onSubmit={() => {
                      setIsProcessing(true);
                      linkScenarioToIncidents({
                        variables: {
                          incidentIds: checked,
                          scenarioId: scenarioId,
                        },
                      }).then(afterSuccess);
                    }}
                  />
                </motion.div>
              )}
              {activeTab === 'from_new' && (
                <FormikProvider value={formik}>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0, duration: 0.1 }}
                  >
                    <IncidentForm
                      buttonTitle="CREATE"
                      onSubmitCb={() =>
                        mpEvent(MPEvents.ButtonClick, {
                          button: 'Create',
                          modal: 'Link incidents to scenario modal',
                          tags: ['SCENARIO', 'INCIDENT'],
                        })
                      }
                    />
                  </motion.div>
                </FormikProvider>
              )}
            </div>
          </div>
          <div className="h-padding">
            {(!!err || !!responseErr) && (
              <>
                <Spacer $px={15} />
                <div className="error">{err || responseErr}</div>
              </>
            )}
          </div>
          <Spacer $px={30} />
        </StyledModal>
      }
    />
  );
};
